/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Skeleton,
  useTheme,
} from '@mui/material';
import { ShoppingCart, FavoriteBorder, Delete } from '@mui/icons-material';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../cart/CartManager';

import CartConfirmationDialog from '../components/shop/CartConfirmationDialog';
import { Product } from '../types/product';

const Wishlist: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { wishlistItems, isLoading, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const [cartConfirmationOpen, setCartConfirmationOpen] = useState(false);
  const [addedProduct, setAddedProduct] = useState<Product | null>(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleRemoveFromWishlist = async (wishlistId: number) => {
    try {
      await removeFromWishlist(wishlistId);
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  };

  const handleRemoveAllItems = async () => {
    if (wishlistItems.length === 0) return;
    
    try {
      // Remove all items one by one
      await Promise.all(
        wishlistItems.map(item => removeFromWishlist(item.id))
      );
    } catch (error) {
      console.error('Error removing all items from wishlist:', error);
    }
  };

  const handleAddToCart = async (productId: number) => {
    try {
      const wishlistItem = wishlistItems.find(item => item.productId === productId);
      const maxAvailable = (wishlistItem?.product as any)?.inventory?.available || (wishlistItem?.product as any)?.quantity;
      const inStock = (wishlistItem?.product as any)?.inventory?.inStock || (wishlistItem?.product as any)?.inStock;
      const available = (wishlistItem?.product as any)?.inventory?.available || (wishlistItem?.product as any)?.quantity;
      
      addToCart({ 
        id: productId, 
        quantity: 1,
        maxAvailable: maxAvailable,
        inStock: inStock,
        inventory: {
          available: available,
          inStock: inStock,
          locations: []
        }
      });
      
      // Find the product details to show in the dialog
      if (wishlistItem && wishlistItem.product) {
        setAddedProduct({
          id: wishlistItem.product.id,
          name: wishlistItem.product.name,
          brand: undefined,
          vendor: undefined,
          imageUrl: wishlistItem.product.imageUrl,
          price: wishlistItem.product.price,
          quantity: 1,
          description: wishlistItem.product.description,
          imageGallery: [],
          comparePrice: undefined,
          nameZh: undefined,
          nameCn: undefined,
          descriptionZh: undefined,
          descriptionCn: undefined,
          brandInfo: undefined,
          categories: undefined,
          trackQuantity: false,
          chargeTax: false,
          continueSelling: false,
          type: undefined,
          brandId: undefined,
          collections: undefined,
          tags: undefined,
          variantOptions: [],
          variants: [],
          isPhysicalProduct: false,
          weight: undefined,
          weightUnit: undefined,
          status: undefined,
          onlineStore: false,
          pointOfSale: false,
          ourPick: false,
          eat365ProductId: undefined,
          createdAt: undefined,
          category: undefined,
          inStock: false,
          sku: undefined,
          barcode: undefined,
          costPerItem: undefined,
          inventory: undefined
        });
        setCartConfirmationOpen(true);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
  };

  if (isLoading) {
    return (
      <Container sx={{ maxWidth: 'lg', margin: '0 auto', p: { xs: 2, md: 3 } }}>
        <Typography sx={{ mb: 3, fontWeight: 700 }}>
          {t('profile.quickActions.wishlist')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                border: '1px solid #E8E8E8',
                borderRadius: 2,
                p: 2,
                backgroundColor: '#fff',
              }}
            >
              <Skeleton variant="rectangular" width={80} height={80} sx={{ mr: 2, borderRadius: 1 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" height={24} sx={{ mb: 1, width: '60%' }} />
                <Skeleton variant="text" height={16} sx={{ mb: 1, width: '80%' }} />
                <Skeleton variant="text" height={20} sx={{ mb: 2, width: '40%' }} />
                <Skeleton variant="rectangular" height={36} width="100%" />
                          </Box>
          </Box>
        ))}
        
        {/* Remove All Button */}
        {wishlistItems.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 3,
              pt: 2,
              borderTop: '1px solid #E8E8E8',
            }}
          >
            <Button
              onClick={handleRemoveAllItems}
              startIcon={<Delete />}
              sx={{
                color: '#d32f2f',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                },
              }}
            >
              Remove all item{wishlistItems.length > 1 ? 's' : ''}
            </Button>
          </Box>
        )}
      </Box>
      </Container>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <Container sx={{ maxWidth: 'lg', margin: '0 auto', p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <FavoriteBorder sx={{ fontSize: 80, color: 'text.secondary' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('wishlist.empty')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('wishlist.emptyDescription')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{
              backgroundColor: theme.palette.custom.brown,
              '&:hover': {
                backgroundColor: theme.palette.custom.brown,
                opacity: 0.9,
              },
            }}
          >
            {t('wishlist.startShopping')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ maxWidth: 'lg', margin: '0 auto', p: { xs: 2, md: 3 } }}>
      <Typography sx={{ mb: 3, fontWeight: 700 }}>
        {t('profile.quickActions.wishlist')} ({wishlistItems.length})
      </Typography>
      
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        {wishlistItems.map((item) => (
          <Box key={item.id}>
            <Box
              sx={{
                display: 'flex',
                border: '1px solid #E8E8E8',
                borderRadius: 2,
                p: 2,
                position: 'relative',
                backgroundColor: '#fff',
                height: '100%',
              }}
            >
              {/* Product Image */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  minWidth: 80,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  mr: 2,
                }}
                onClick={() => handleProductClick(item.productId)}
              >
                <img
                  src={item.product?.imageUrl || '/images/img_placeholder.svg'}
                  alt={item.product?.name || 'Product'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>

              {/* Product Details */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Product Name */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    cursor: 'pointer',
                    fontSize: '16px',
                    '&:hover': { color: theme.palette.custom.brown },
                  }}
                  onClick={() => handleProductClick(item.productId)}
                >
                  {item.product?.name || 'Product Name'}
                </Typography>

                {/* Product Description */}
                <Typography
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    fontSize: '14px',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.product?.description || 'Product description'}
                </Typography>

                {/* Pricing */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 600,
                      fontSize: '18px',
                    }}
                  >
                    ${item.product?.price || '0.00'}
                  </Typography>
                </Box>

                {/* Add to Cart Button */}
                <Button
                  variant="outlined"
                  startIcon={<ShoppingCart />}
                  onClick={() => handleAddToCart(item.productId)}
                  sx={{
                    width: '100%',
                    borderColor: theme.palette.custom.brown,
                    color: theme.palette.custom.brown,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: theme.palette.custom.brown,
                      backgroundColor: theme.palette.custom.brown,
                      color: 'white',
                    },
                  }}
                >
                  Add to Cart
                </Button>
              </Box>

              {/* Remove Icon */}
              <IconButton
                onClick={() => handleRemoveFromWishlist(item.id)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#131518',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    lineHeight: 1,
                  }}
                >
                  ×
                </Box>
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
      
      {/* Remove All Button - Outside the grid */}
      {wishlistItems.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mt: 3,
            pt: 2,
            borderTop: '1px solid #E8E8E8',
          }}
        >
          <Button
            onClick={handleRemoveAllItems}
            startIcon={<Delete />}
            sx={{
              color: theme.palette.custom.brown,
              textTransform: 'none',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: 'rgba(180, 139, 106, 0.1)',
              },
            }}
          >
                      Remove all item{wishlistItems.length > 1 ? 's' : ''}
        </Button>
      </Box>
    )}

    {/* Cart Confirmation Dialog */}
    <CartConfirmationDialog
      open={cartConfirmationOpen}
      onClose={() => setCartConfirmationOpen(false)}
      product={addedProduct}
    />
  </Container>
);
};

export default Wishlist;
